import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MdSearch, MdClose, MdHome, MdSubdirectoryArrowLeft } from "react-icons/md";

const MAX_SUGGESTIONS = 6;

function Highlight({ name, query }) {
	const start = name.toLowerCase().indexOf(query.toLowerCase());
	if (!query || start < 0) return name;
	const end = start + query.length;
	return (
		<>
			{name.slice(0, start)}
			<mark>{name.slice(start, end)}</mark>
			{name.slice(end)}
		</>
	);
}

export default function Searchform(props) {
	const navigate = useNavigate();
	const { data = [], type } = props;
	const wrapperRef = useRef(null);
	const [text, setText] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [isSearching, setIsSearching] = useState(false);
	const [notFound, setNotFound] = useState(false);
	const query = text.trim();

	const suggestions = useMemo(() => {
		if (!query) return [];
		const q = query.toLowerCase();
		return (data || [])
			.filter((item) => item?.name?.toLowerCase().includes(q))
			.slice(0, MAX_SUGGESTIONS);
	}, [data, query]);

	useEffect(() => {
		const handleClick = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const onChangeHandler = (value) => {
		setText(value);
		setActiveIndex(-1);
		setNotFound(false);
		setIsOpen(value.trim().length > 0);
	};

	const onSuggestion = (item) => {
		setText(item.name);
		setIsOpen(false);
		navigate(`details/${item.slug}`);
	};

	const handleSearch = async () => {
		if (!query) return;
		if (activeIndex >= 0 && suggestions[activeIndex]) return onSuggestion(suggestions[activeIndex]);
		if (suggestions.length > 0) return onSuggestion(suggestions[0]);

		setIsSearching(true);
		try {
			const res = await axios({
				method: "post",
				url: `${process.env.REACT_APP_API_URL}/api/v1/residences/search`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({ search: query }),
			});
			const match = res.data?.data?.[0];
			if (match?.slug) {
				setIsOpen(false);
				navigate(`details/${match.slug}`);
			} else {
				setNotFound(true);
				setIsOpen(true);
			}
		} catch (err) {
			console.log(err);
			setNotFound(true);
			setIsOpen(true);
		} finally {
			setIsSearching(false);
		}
	};

	const onKeyDown = (e) => {
		if (e.key === "ArrowDown" && suggestions.length) {
			e.preventDefault();
			setIsOpen(true);
			setActiveIndex((i) => (i + 1) % suggestions.length);
		} else if (e.key === "ArrowUp" && suggestions.length) {
			e.preventDefault();
			setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
		} else if (e.key === "Escape") {
			setIsOpen(false);
			setActiveIndex(-1);
		}
	};

	const clear = () => {
		setText("");
		setIsOpen(false);
		setNotFound(false);
		setActiveIndex(-1);
	};

	const showDropdown = isOpen && query.length > 0;

	return (
		<div className="as" ref={wrapperRef}>
			<form
				className="as-field"
				role="search"
				onSubmit={(e) => {
					e.preventDefault();
					handleSearch();
				}}
			>
				<MdSearch className="as-icon" aria-hidden="true" />
				<input
					type="search"
					className="as-input"
					value={text}
					onChange={(e) => onChangeHandler(e.target.value)}
					onFocus={() => query && setIsOpen(true)}
					onKeyDown={onKeyDown}
					placeholder={`Search ${type} by name`}
					aria-label={`Search ${type} by name`}
					role="combobox"
					aria-expanded={showDropdown}
					aria-controls="as-listbox"
					aria-autocomplete="list"
					aria-activedescendant={activeIndex >= 0 ? `as-option-${activeIndex}` : undefined}
					autoComplete="off"
				/>
				{text && (
					<button type="button" className="as-clear" onClick={clear} aria-label="Clear search">
						<MdClose />
					</button>
				)}
				<button type="submit" className="as-submit" disabled={isSearching}>
					{isSearching ? <span className="ad-spinner" aria-hidden="true" /> : "Search"}
				</button>
			</form>

			{showDropdown && (
				<div className="as-dropdown">
					{suggestions.length > 0 ? (
						<ul id="as-listbox" role="listbox" className="as-list">
							{suggestions.map((item, index) => (
								<li
									key={item?._id || item?.slug}
									id={`as-option-${index}`}
									role="option"
									aria-selected={index === activeIndex}
									className={`as-option${index === activeIndex ? " is-active" : ""}`}
									onMouseEnter={() => setActiveIndex(index)}
									onMouseDown={(e) => {
										e.preventDefault();
										onSuggestion(item);
									}}
								>
									<MdHome className="as-option-icon" aria-hidden="true" />
									<span className="as-option-text">
										<Highlight name={item.name} query={query} />
									</span>
									<MdSubdirectoryArrowLeft className="as-option-enter" aria-hidden="true" />
								</li>
							))}
						</ul>
					) : (
						<p className="as-empty">
							{notFound
								? `No ${String(type).toLowerCase()} found for "${query}"`
								: `Press Enter to search for "${query}"`}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
